import { z } from 'zod';

/**
 * Dashboard-managed vault credentials (#312, milestone v1.3 connections).
 *
 * A maintainer saves a named credential in the dashboard; `.maintainer.yml`
 * references it as `token_ref: dashboard:<handle>` (the `ns:name` RefName
 * grammar, #711) — e.g. the bearer token an `mcp_servers[]` entry connects
 * with. The agent side already resolves any `secrets.name` at session start
 * (`buildMcpTokenResolver`), so this schema is the produce half only.
 *
 * The `dashboard:` namespace keeps user handles from colliding with
 * connector-managed rows (`integration:<handle>`, see `integrationSecretName`).
 */
export const DASHBOARD_SECRET_PREFIX = 'dashboard:';

/**
 * The user-chosen handle (the part after `dashboard:`). Same base shape as a
 * policy RefName segment; capped so the full `dashboard:<handle>` ref stays
 * within RefName's 64-char limit.
 */
export const DashboardSecretHandleSchema = z
  .string()
  .min(1)
  .max(64 - DASHBOARD_SECRET_PREFIX.length)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'lowercase, digits, _ or -');

/**
 * The full dashboard-managed vault ref (`dashboard:<handle>`) — what
 * `DELETE /v1/secrets/:name` accepts. Deliberately rejects `integration:<handle>`
 * (connector-managed) and bare handles.
 */
export const DashboardSecretNameSchema = z
  .string()
  .max(64)
  .regex(/^dashboard:[a-z0-9][a-z0-9_-]*$/, 'expected dashboard:<handle>');

/** `POST /v1/secrets` payload. The value is sealed on arrival, never echoed. */
export const CreateDashboardSecretSchema = z
  .object({
    name: DashboardSecretHandleSchema,
    value: z.string().min(1).max(8192),
  })
  .strict();

/** Vault `secrets.name` for a dashboard-managed credential. */
export function dashboardSecretName(handle: string): string {
  return `${DASHBOARD_SECRET_PREFIX}${handle}`;
}

/** Is this vault row dashboard-managed (vs connector-managed `integration:*`)? */
export function isDashboardSecretName(name: string): boolean {
  return name.startsWith(DASHBOARD_SECRET_PREFIX);
}
